// src/pages/Equipment/BulkReturnActsModal.jsx
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Modal, Table, Button, Tag, Space, Typography,
  Tooltip, Badge, Divider
} from 'antd';
import { RollbackOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { employeesAPI } from '../../api/client';
import ReturnActModal from '../Employees/ReturnActModal';

const { Text } = Typography;

/**
 * BulkReturnActsModal
 *
 * Показывается когда bulk «Вернуть на склад» затронул оборудование
 * нескольких разных ответственных.
 *
 * groups: Array<{
 *   responsible_id:   string
 *   responsible_name: string
 *   equipment:        EquipmentItem[]
 * }>
 *
 * Пользователь видит список сотрудников → кнопка «Акт» рядом с каждым →
 * открывает ReturnActModal для конкретного сотрудника.
 * Уже сформированные отмечаются галочкой.
 */
export default function BulkReturnActsModal({ open, groups, onClose }) {
  // id сотрудника, для которого сейчас открываем ReturnActModal
  const [activeEmployeeId, setActiveEmployeeId] = useState(null);
  const [activeEquipment,  setActiveEquipment]  = useState([]);
  const [activeEmployee,   setActiveEmployee]   = useState(null);
  const [showReturnAct,    setShowReturnAct]     = useState(false);

  // Отслеживаем для каких сотрудников акт уже сформирован
  const [doneIds, setDoneIds] = useState([]);

  const handleOpenAct = async (group) => {
    try {
      const res = await employeesAPI.getOne(group.responsible_id);
      setActiveEmployee(res.data);
      setActiveEquipment(group.equipment);
      setActiveEmployeeId(group.responsible_id);
      setShowReturnAct(true);
    } catch {
      // если не удалось загрузить детали — используем то что есть
      setActiveEmployee({ id: group.responsible_id, full_name: group.responsible_name, companies: [] });
      setActiveEquipment(group.equipment);
      setActiveEmployeeId(group.responsible_id);
      setShowReturnAct(true);
    }
  };

  const handleReturnActClose = () => {
    setShowReturnAct(false);
    // Помечаем как выполненный
    if (activeEmployeeId) {
      setDoneIds(prev => [...new Set([...prev, activeEmployeeId])]);
    }
    setActiveEmployee(null);
    setActiveEquipment([]);
    setActiveEmployeeId(null);
  };

  const handleMainClose = () => {
    setDoneIds([]);
    onClose();
  };

  const allDone = groups.length > 0 && groups.every(g => doneIds.includes(g.responsible_id));

  const columns = [
    {
      title: 'Сотрудник',
      dataIndex: 'responsible_name',
      key: 'name',
      render: (v, record) => (
        <Space>
          {doneIds.includes(record.responsible_id)
            ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
            : <RollbackOutlined style={{ color: '#888' }} />
          }
          <Text strong={!doneIds.includes(record.responsible_id)}
                type={doneIds.includes(record.responsible_id) ? 'secondary' : undefined}>
            {v}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Позиций',
      key: 'count',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <Badge
          count={record.equipment.length}
          color={doneIds.includes(record.responsible_id) ? '#d9d9d9' : '#fa8c16'}
          style={{ fontSize: 12 }}
        />
      ),
    },
    {
      title: 'Оборудование',
      key: 'equipment',
      render: (_, record) => (
        <Space wrap size={4}>
          {record.equipment.slice(0, 4).map(e => (
            <Tooltip key={e.id} title={e.name}>
              <Tag style={{ fontSize: 11, margin: 0 }}>{e.inventory_number}</Tag>
            </Tooltip>
          ))}
          {record.equipment.length > 4 && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              +{record.equipment.length - 4} ещё
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 120,
      render: (_, record) => (
        doneIds.includes(record.responsible_id)
          ? <Tag color="success" icon={<CheckCircleOutlined />}>Готово</Tag>
          : (
            <Button
              type="primary"
              size="small"
              icon={<RollbackOutlined />}
              onClick={() => handleOpenAct(record)}
            >
              Акт
            </Button>
          )
      ),
    },
  ];

  return (
    <>
      <Modal
        open={open}
        title={
          <Space>
            <RollbackOutlined />
            <span>Акты возврата — {groups.length} сотрудников</span>
          </Space>
        }
        onCancel={handleMainClose}
        footer={[
          <Button key="close" type={allDone ? 'primary' : 'default'} onClick={handleMainClose}>
            {allDone ? 'Готово' : 'Закрыть'}
          </Button>,
        ]}
        width={680}
      >
        <div style={{ marginBottom: 12, color: '#888', fontSize: 13 }}>
          Оборудование возвращено от нескольких сотрудников. Выберите для кого сформировать акт возврата.
        </div>

        <Table
          dataSource={groups}
          columns={columns}
          rowKey="responsible_id"
          size="small"
          pagination={false}
          rowClassName={(r) => doneIds.includes(r.responsible_id) ? 'row-decommissioned' : ''}
        />

        {allDone && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <div style={{ textAlign: 'center', color: '#52c41a' }}>
              <CheckCircleOutlined style={{ marginRight: 6 }} />
              Все акты сформированы
            </div>
          </>
        )}
      </Modal>

      {/* ReturnActModal для конкретного сотрудника */}
      {activeEmployee && (
        <ReturnActModal
          open={showReturnAct}
          onClose={handleReturnActClose}
          employee={activeEmployee}
          equipment={activeEquipment}
        />
      )}
    </>
  );
}